/**
 * RestrictedAccess — full-page state shown when an authenticated user
 * lands on a route their plan / role does not allow.
 *
 * Iter 332 — Cleaned up customer-facing copy. No more raw plan keys
 * (PERSONAL_PREMIUM / business_suite_access), no role identifiers, no
 * feature identifiers. Just plain language plus a primary "Change
 * account type" CTA (Account Type and Plan are different concepts —
 * what's actually broken for the customer 90% of the time is that
 * they're on the wrong *track*, not that they need to pay).
 */
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/lib/i18n';
import { useEntitlements } from '@/lib/entitlements';
import { Lock, ArrowLeft, Users, Mail, ShieldAlert } from 'lucide-react';
import PreservedReadOnly from '@/components/dashboard/PreservedReadOnly';

export function RestrictedAccess({ featureKey, reason = 'plan' }) {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const navigate = useNavigate();
  const ent = useEntitlements();
  const hint = ent.hint(featureKey);

  // reason: 'plan' (default) → "wrong account type" CTA
  //         'role' → "contact admin" CTA
  const isRoleReason = reason === 'role';

  // Iter 334i — Feature-specific friendly copy. The generic backend
  // hint ("This feature requires a higher plan.") is misleading for
  // tools that are blocked by ACCOUNT TYPE, not plan tier. For known
  // account-type-only features we override with explicit language so
  // a Personal Premium customer understands the difference.
  const ACCOUNT_TYPE_GATED = {
    bulk_reports: {
      en: 'Bulk Reports are available for Business accounts and approved Government & Public-Sector accounts. Your Personal Premium plan is active — switching account type unlocks this tool.',
      ja: '一括レポートは法人アカウント、または承認された官公庁・公共部門アカウントでご利用いただけます。先行受付・プレミアムは有効です。アカウントタイプを変更するとご利用いただけます。',
    },
    vehicle_monitoring: {
      en: 'Vehicle Monitoring is available for Business accounts and approved Government & Public-Sector accounts. Switching account type unlocks this tool.',
      ja: '車両モニタリングは法人または承認された官公庁・公共部門アカウントでご利用いただけます。アカウントタイプを変更するとご利用いただけます。',
    },
    manager_review_queue: {
      en: 'Manager Review is available for Business accounts and approved Government & Public-Sector accounts.',
      ja: 'マネージャーレビューは法人または承認された官公庁・公共部門アカウントでご利用いただけます。',
    },
    business_suite_access: {
      en: 'Business tools are available for Business accounts and approved Government & Public-Sector accounts.',
      ja: '法人向けツールは法人または承認された官公庁・公共部門アカウントでご利用いただけます。',
    },
    authorized_vehicle_registry: {
      en: 'Authorized vehicle controls are available for Business and approved Government & Public-Sector accounts.',
      ja: '承認車両管理は法人または承認された官公庁・公共部門アカウントでご利用いただけます。',
    },
  };
  const overrideHint = ACCOUNT_TYPE_GATED[featureKey];

  // Free Weekly users hitting a PREMIUM-TIER feature (paused by plan tier,
  // not account type) get plan-upgrade framing rather than "wrong account
  // type". Account-type-gated features (Business/Gov, in ACCOUNT_TYPE_GATED)
  // keep their precise account-type copy.
  const isFreeWeekly = ent.plan_group === 'PERSONAL_WEEKLY_FREE';
  const isPremiumTier = !isRoleReason && isFreeWeekly && !overrideHint;
  const premiumHint = ja
    ? 'この機能はプレミアムプランで利用できます。無料週次プランでは週次レポート、PDF/CSV出力、基本的なMEISAI連携をご利用いただけます。以前に設定された内容（アラート・車両・配信ルールなど）は削除されておらず、一時停止中です。アップグレードすると再開されます。'
    : 'This feature is available on Premium plans. Your Free Weekly plan keeps weekly reports, PDF/CSV exports, and basic MEISAI access. Any settings you set up before are paused — not deleted — and resume automatically when you upgrade.';

  // Friendly bilingual fallback for the upgrade hint, used when the
  // backend didn't surface a feature-specific message.
  const friendlyHint = isRoleReason
    ? (ja
        ? 'この操作には別の権限が必要です。組織の管理者にお問い合わせください。'
        : 'This action requires a different role. Please contact your organisation administrator.')
    : overrideHint
        ? (ja ? overrideHint.ja : overrideHint.en)
        : (ja
            ? 'こちらの機能は、お客様の現在のアカウントタイプには含まれていません。先行受付・プレミアムは有効ですが、この機能は別のアカウントタイプ（法人・官公庁など）でご利用いただけます。'
            : "This tool isn't included in your current account type. Your Early Access Premium is active — this feature simply belongs to a different account type (Business or Government & Public-Sector).");

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4" data-testid="restricted-access-page">
      <Card className="rounded-2xl border-2 border-amber-200 bg-amber-50/30 max-w-lg w-full">
        <CardContent className="p-6 sm:p-8 space-y-5">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              {isRoleReason
                ? <ShieldAlert className="w-6 h-6 text-amber-700" data-testid="restricted-icon-role" />
                : <Lock className="w-6 h-6 text-amber-700" data-testid="restricted-icon-plan" />}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-black text-slate-900 leading-tight">
                {isRoleReason
                  ? (ja ? 'この操作には別の権限が必要です'
                        : 'This action requires a different role')
                  : isPremiumTier
                    ? (ja ? 'この機能はプレミアムプランで利用できます'
                          : 'This feature is available on Premium plans')
                    : (ja ? 'この機能はお客様のアカウントタイプには含まれていません'
                          : 'This feature is not included in your current account type')}
              </h1>
              <p className="text-sm text-slate-700 mt-2 leading-relaxed" data-testid="restricted-hint">
                {/* Account-type-gated features bypass the backend hint
                    (which says "requires a higher plan" — incorrect for
                    these features). Other features still defer to the
                    backend hint when present. */}
                {isPremiumTier
                  ? premiumHint
                  : overrideHint
                    ? friendlyHint
                    : ((ja ? hint.ja : hint.en) || friendlyHint)}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            {isRoleReason ? (
              <Button
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => navigate('/dashboard/support')}
                data-testid="restricted-contact-admin-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                {ja ? 'サポートに連絡' : 'Contact support'}
              </Button>
            ) : (
              <Button
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                onClick={() => navigate(isPremiumTier ? '/dashboard/plan?intent=restore-premium' : '/dashboard/plan')}
                data-testid="restricted-change-account-type-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                {isPremiumTier
                  ? (ja ? 'プレミアムを再開' : 'Restore Premium')
                  : (ja ? 'アカウントタイプを確認' : 'View account options')}
              </Button>
            )}
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => navigate('/dashboard')}
              data-testid="restricted-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {ja ? 'ダッシュボードへ戻る' : 'Back to dashboard'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


/**
 * EntitlementGate — wrapper that renders <RestrictedAccess> when the
 * user lacks view (or, optionally, edit) access to a feature. Use to
 * gate entire dashboard routes.
 */
export function EntitlementGate({ feature, mutate = false, children }) {
  const ent = useEntitlements();
  if (!ent.ready) return null;          // wait for entitlements before deciding
  if (ent.is_admin) return children;    // admins / hosts bypass
  if (mutate ? ent.canEdit(feature) : ent.can(feature)) {
    // Downgraded former-premium accounts keep their prior config visible but
    // READ-ONLY (paused). View access is granted via preservation; render the
    // page inside the paused/read-only wrapper instead of letting it edit.
    if (!mutate && ent.preserved(feature)) {
      return <PreservedReadOnly>{children}</PreservedReadOnly>;
    }
    return children;
  }
  // Distinguish between plan-locked vs role-locked
  const featureViewable = ent.can(feature);
  const reason = mutate && featureViewable ? 'role' : 'plan';
  return <RestrictedAccess featureKey={feature} reason={reason} />;
}

export default RestrictedAccess;
